import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdPersonPin,
  MdMedicalServices,
  MdEmail,
  MdPhone,
  MdSave,
  MdCancel,
  MdPhoto,
  MdClose,
  MdSearch,
  MdFilterList,
  MdClear,
  MdVisibility,
  MdNavigateBefore,
  MdNavigateNext,
  MdFirstPage,
  MdLastPage,
  MdPersonSearch,
} from "react-icons/md";
import Modal from "components/modal";
import { usePhotoUrl } from "hooks/usePhotoUrl";
import DoctorService from "service/doctor.service";
import DoctorDetails from "./components/DoctorDetails";

const Doctors = () => {
  // États
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // État pour la visualisation détaillée
  const [detailView, setDetailView] = useState(false);
  const [doctorToView, setDoctorToView] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Recherche et filtrage
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSpecialization, setFilterSpecialization] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // État du formulaire
  const [formData, setFormData] = useState({
    titre: "Dr",
    name: "",
    specialization: "",
    phone: "",
    email: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  // Récupérer toutes les spécialités uniques des médecins
  const specializations = useMemo(() => {
    const uniqueSpecs = [
      ...new Set(doctors.map((doctor) => doctor.specialization)),
    ];
    return uniqueSpecs.filter(Boolean).sort();
  }, [doctors]);

  // Utilisation du hook personnalisé pour gérer les URLs des photos
  const { getPhotoUrl } = usePhotoUrl();

  // Effets
  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    filterDoctors();
  }, [doctors, searchTerm, filterSpecialization]);

  // Médecins filtrés et paginés
  const totalPages = useMemo(
    () => Math.ceil(filteredDoctors.length / itemsPerPage),
    [filteredDoctors, itemsPerPage]
  );

  const currentDoctors = useMemo(
    () =>
      filteredDoctors.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [filteredDoctors, currentPage, itemsPerPage]
  );

  // Fonctions
  const fetchDoctors = async () => {
    setIsLoading(true);
    try {
      const data = await DoctorService.getAllDoctors();
      setDoctors(data);
      setFilteredDoctors(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des médecins:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de récupérer les médecins. Veuillez réessayer plus tard.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterDoctors = () => {
    let results = [...doctors];

    // Filtrage par spécialité
    if (filterSpecialization !== "all") {
      results = results.filter(
        (doctor) => doctor.specialization === filterSpecialization
      );
    }

    // Filtrage par terme de recherche
    if (searchTerm.trim() !== "") {
      const searchTermLower = searchTerm.toLowerCase().trim();
      results = results.filter(
        (doctor) =>
          doctor.name.toLowerCase().includes(searchTermLower) ||
          doctor.email.toLowerCase().includes(searchTermLower) ||
          doctor.specialization.toLowerCase().includes(searchTermLower)
      );
    }

    setFilteredDoctors(results);

    // Retour à la première page lors d'un changement de filtre
    if (currentPage > 1) {
      setCurrentPage(1);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterSpecialization("all");
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        photo: file,
      });

      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handlePhotoUploadClick = () => {
    document.getElementById("photo").click();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      document.getElementById("photo").click();
    }
  };

  const resetForm = () => {
    setFormData({
      titre: "Dr",
      name: "",
      specialization: "",
      phone: "",
      email: "",
      photo: null,
    });
    setPhotoPreview(null);
    setSelectedDoctor(null);
  };

  const openForm = (doctor = null) => {
    if (doctor) {
      setFormData({
        titre: doctor.titre || "Dr",
        name: doctor.name,
        specialization: doctor.specialization,
        phone: doctor.phone,
        email: doctor.email,
        photo: null, // We can't set the file input value for security reasons
      });
      setSelectedDoctor(doctor);
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const viewDoctorDetails = (doctor) => {
    setDoctorToView(doctor);
    setDetailView(true);
  };

  const closeDoctorDetails = () => {
    setDoctorToView(null);
    setDetailView(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation basique
    if (
      !formData.name ||
      !formData.specialization ||
      !formData.email ||
      !formData.phone
    ) {
      Swal.fire({
        icon: "warning",
        title: "Champs requis",
        text: "Veuillez remplir tous les champs obligatoires.",
        confirmButtonColor: "#2ca01c",
      });
      setIsSubmitting(false);
      return;
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: "warning",
        title: "Email invalide",
        text: "Veuillez entrer une adresse email valide.",
        confirmButtonColor: "#2ca01c",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      if (selectedDoctor) {
        // Mettre à jour un médecin existant
        await DoctorService.updateDoctor(selectedDoctor.id, formData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le médecin a été mis à jour avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      } else {
        // Créer un nouveau médecin
        await DoctorService.createDoctor(formData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Le médecin a été ajouté avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      }

      // Rafraîchir la liste des médecins
      fetchDoctors();
      closeForm();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du médecin:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Une erreur est survenue lors de l'enregistrement du médecin.",
        confirmButtonColor: "#2ca01c",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2ca01c",
      cancelButtonColor: "#e21b1b",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await DoctorService.deleteDoctor(id);
          setDoctors(doctors.filter((doctor) => doctor.id !== id));
          setFilteredDoctors(
            filteredDoctors.filter((doctor) => doctor.id !== id)
          );

          Swal.fire({
            title: "Supprimé!",
            text: "Le médecin a été supprimé avec succès.",
            icon: "success",
            confirmButtonColor: "#2ca01c",
          });
        } catch (error) {
          console.error("Erreur lors de la suppression du médecin:", error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Une erreur est survenue lors de la suppression du médecin.",
            confirmButtonColor: "#2ca01c",
          });
        }
      }
    });
  };

  // Fonctions de pagination
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(currentPage - 1);
  const goToNextPage = () => setCurrentPage(currentPage + 1);
  const goToLastPage = () => setCurrentPage(totalPages);

  // Composants de rendu
  const renderDoctorCard = (doctor) => (
    <div
      key={doctor.id}
      className="overflow-hidden rounded-lg bg-white  transition-all duration-300 hover:shadow-xl"
    >
      <div className="relative h-48">
        {doctor.photo ? (
          <img
            src={getPhotoUrl(doctor.photo)}
            alt={doctor.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10">
            <MdPersonPin className="text-6xl text-[#2ca01c]/40" />
          </div>
        )}
        <div className="from-black/60 to-transparent absolute bottom-0 left-0 w-full bg-gradient-to-t p-3">
          <span className="rounded-full bg-blue-500/90 px-3 py-1 text-xs font-medium text-white">
            {doctor.specialization}
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="mb-2 text-xl font-bold text-gray-800">
          {doctor.titre} {doctor.name}
        </h3>
        <div className="mb-4 space-y-1 text-sm text-gray-600">
          <p className="flex items-center gap-2">
            <MdEmail className="text-[#7028a9]" /> {doctor.email}
          </p>
          <p className="flex items-center gap-2">
            <MdPhone className="text-[#7028a9]" /> {doctor.phone}
          </p>
        </div>
        <div className="flex flex-col space-y-2">
          <button
            className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
            onClick={() => viewDoctorDetails(doctor)}
          >
            <MdVisibility className="mr-1" /> Voir la fiche
          </button>
          <div className="flex space-x-3">
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
              onClick={() => openForm(doctor)}
            >
              <MdEdit className="mr-1" /> Modifier
            </button>
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
              onClick={() => handleDelete(doctor.id)}
            >
              <MdDelete className="mr-1" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPagination = () => {
    return (
      <div className="mt-8 flex items-center justify-center">
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={goToFirstPage}
            disabled={currentPage === 1}
            className={`${
              currentPage === 1
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center rounded-l-md border border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Première page"
          >
            <MdFirstPage />
          </button>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className={`${
              currentPage === 1
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Page précédente"
          >
            <MdNavigateBefore />
          </button>

          <div className="flex items-center border-b border-r border-t border-gray-300 bg-white px-4 py-2 text-sm font-medium">
            <span className="mx-2 font-medium text-gray-700">
              Page {currentPage} sur {totalPages || 1}
            </span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`${
              currentPage === totalPages || totalPages === 0
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Page suivante"
          >
            <MdNavigateNext />
          </button>
          <button
            onClick={goToLastPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`${
              currentPage === totalPages || totalPages === 0
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-white text-gray-700 hover:bg-gray-50"
            } inline-flex items-center rounded-r-md border-b border-r border-t border-gray-300 px-3 py-2 text-sm font-medium`}
            aria-label="Dernière page"
          >
            <MdLastPage />
          </button>
        </div>
      </div>
    );
  };

  const renderSearchFilters = () => {
    return (
      <div className="mb-6 rounded-lg bg-white p-4 ">
        <div className="mb-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          {/* Champ de recherche */}
          <div className="relative w-full max-w-md">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 transform text-lg text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un médecin..."
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                aria-label="Effacer la recherche"
              >
                <MdClear />
              </button>
            )}
          </div>

          {/* Bouton filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center rounded-md border ${
              showFilters
                ? "border-[#2ca01c] bg-[#2ca01c]/10 text-[#2ca01c]"
                : "border-gray-300 bg-white text-gray-700"
            } px-4 py-2 transition-colors`}
          >
            <MdFilterList className="mr-2" />
            Filtres{" "}
            {filterSpecialization !== "all" && (
              <span className="ml-1 rounded-full bg-[#2ca01c] px-2 text-white">
                1
              </span>
            )}
          </button>

          {/* Bouton réinitialiser */}
          {(searchTerm || filterSpecialization !== "all") && (
            <button
              onClick={resetFilters}
              className="flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              <MdClear className="mr-2" />
              Réinitialiser
            </button>
          )}
        </div>

        {/* Options de filtrage par spécialité */}
        {showFilters && (
          <div className="animate-fadeIn mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 font-medium text-gray-700">
              Filtrer par spécialité:
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterSpecialization("all")}
                className={`rounded-md px-3 py-1 text-sm ${
                  filterSpecialization === "all"
                    ? "bg-[#2ca01c] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Toutes les spécialités
              </button>

              {specializations.map((spec) => (
                <button
                  key={spec}
                  onClick={() => setFilterSpecialization(spec)}
                  className={`rounded-md px-3 py-1 text-sm ${
                    filterSpecialization === spec
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Résumé des résultats */}
        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-sm text-gray-500">
          <p>
            {filteredDoctors.length} médecin
            {filteredDoctors.length !== 1 ? "s" : ""} trouvé
            {filteredDoctors.length !== 1 ? "s" : ""}
            {searchTerm || filterSpecialization !== "all"
              ? " avec les filtres actuels"
              : ""}
          </p>

          {filteredDoctors.length > 0 && (
            <div className="flex items-center">
              <p className="text-xs text-gray-500">
                Affichage{" "}
                {Math.min(
                  (currentPage - 1) * itemsPerPage + 1,
                  filteredDoctors.length
                )}{" "}
                - {Math.min(currentPage * itemsPerPage, filteredDoctors.length)}{" "}
                sur {filteredDoctors.length}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Contenu du formulaire pour la modal
  const formContent = (
    <>
      {/* En-tête du formulaire */}
      <div className="mb-5 border-b border-gray-200 pb-5">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#333333]">
            {selectedDoctor ? "Modifier le médecin" : "Ajouter un médecin"}
          </h2>
          <button
            onClick={closeForm}
            className="rounded-full bg-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-300"
            aria-label="Fermer"
          >
            <MdClose size={20} />
          </button>
        </div>
      </div>

      {/* Corps du formulaire */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <label
              htmlFor="titre"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Titre <span className="text-[#e21b1b]">*</span>
            </label>
            <select
              id="titre"
              name="titre"
              value={formData.titre}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              required
            >
              <option value="Dr">Dr</option>
              <option value="Pr">Pr</option>
              <option value="Mr">Mr</option>
              <option value="Mme">Mme</option>
              <option value="Mlle">Mlle</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="name"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Nom <span className="text-[#e21b1b]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              placeholder="Nom complet"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="specialization"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Spécialisation <span className="text-[#e21b1b]">*</span>
          </label>
          <input
            type="text"
            id="specialization"
            name="specialization"
            value={formData.specialization}
            onChange={handleInputChange}
            className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
            placeholder="Cardiologie, Pédiatrie, etc."
            required
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Email <span className="text-[#e21b1b]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              placeholder="docteur@example.com"
              required
            />
          </div>

          <div>
            <label
              htmlFor="phone"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Téléphone <span className="text-[#e21b1b]">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20"
              placeholder="1234567890"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="photo"
            className="mb-1 flex items-center text-sm font-semibold text-gray-700"
          >
            <MdPhoto className="mr-1 text-[#7028a9]" /> Photo
          </label>
          <div className="flex items-center">
            <button
              type="button"
              onClick={handlePhotoUploadClick}
              onKeyDown={handleKeyDown}
              tabIndex="0"
              aria-label="Choisir une photo"
              role="button"
              className="flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2ca01c]"
            >
              <MdPhoto className="mb-2 text-3xl text-gray-400" />
              <span className="text-sm text-gray-500">
                Cliquez pour ajouter une photo du médecin
              </span>
            </button>
            <input
              type="file"
              id="photo"
              name="photo"
              onChange={handlePhotoChange}
              accept="image/*"
              className="hidden"
              aria-hidden="true"
            />
          </div>
          {photoPreview && (
            <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Aperçu de l'image sélectionnée"
                  className="h-48 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPhotoPreview(null);
                    setFormData({
                      ...formData,
                      photo: null,
                    });
                    document.getElementById("photo").value = "";
                  }}
                  className="absolute right-2 top-2 rounded-full bg-white/80 p-1 text-gray-700 hover:bg-white"
                  aria-label="Supprimer l'image"
                >
                  <MdCancel />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Boutons du formulaire */}
        <div className="mt-8 flex justify-end space-x-3 border-t pt-5">
          <button
            type="button"
            onClick={closeForm}
            className="flex items-center rounded-md border border-gray-300 bg-white px-5 py-2 text-gray-700 transition-colors duration-200 hover:bg-gray-100"
          >
            <MdCancel className="mr-2" /> Annuler
          </button>

          <button
            type="submit"
            className="flex items-center justify-center rounded-md bg-gradient-to-r from-[#2ca01c] to-[#238615] px-5 py-2 text-white  transition-all duration-200 hover:from-[#238615] hover:to-[#1c6e11] hover:shadow-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="mr-2 h-5 w-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
                Traitement...
              </>
            ) : (
              <>
                <MdSave className="mr-2" />
                {selectedDoctor ? "Mettre à jour" : "Enregistrer"}
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );

  // Rendu principal
  return (
    <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {detailView && doctorToView ? (
        <div className="rounded-lg bg-white p-6 ">
          <DoctorDetails
            doctor={doctorToView}
            onClose={closeDoctorDetails}
            getPhotoUrl={getPhotoUrl}
          />
        </div>
      ) : (
        <>
          {/* Titre de la page */}
          <div className="flex flex-col items-start justify-between rounded-lg bg-white p-6  sm:flex-row sm:items-center">
            <h1 className="mb-4 flex items-center text-3xl font-bold text-[#333333] sm:mb-0">
              <div className="mr-4 rounded-full bg-blue-500 p-2 text-white">
                <MdMedicalServices className="text-3xl" />
              </div>
              Médecins
              <span className="ml-2 text-sm font-normal text-gray-500">
                Centre Hospitalier Universitaire Tambohobe
              </span>
            </h1>

            <button
              className="group flex items-center rounded-full bg-[#2ca01c] px-6 py-3 text-white  transition-all duration-300 hover:bg-[#238615] hover:shadow-lg"
              onClick={() => openForm()}
            >
              <MdAdd className="mr-2 rounded-full bg-white text-xl text-[#2ca01c] group-hover:bg-[#f0f0f0]" />
              Ajouter un médecin
            </button>
          </div>

          {/* Recherche et filtres */}
          {renderSearchFilters()}

          {/* Liste des médecins */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-b-transparent h-16 w-16 animate-spin rounded-full border-4 border-[#2ca01c]"></div>
            </div>
          ) : (
            <>
              {filteredDoctors.length === 0 ? (
                <div className="rounded-lg bg-white p-8 text-center ">
                  <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-blue-50">
                    <MdPersonSearch className="text-5xl text-blue-500" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">
                    {doctors.length === 0
                      ? "Aucun médecin enregistré"
                      : "Aucun médecin ne correspond à vos critères de recherche"}
                  </h3>
                  <p className="text-gray-600">
                    {doctors.length === 0
                      ? 'Ajoutez votre premier médecin en cliquant sur le bouton "Ajouter un médecin"'
                      : "Veuillez essayer avec différents termes de recherche ou filtres"}
                  </p>
                  {doctors.length > 0 &&
                    (searchTerm || filterSpecialization !== "all") && (
                      <button
                        onClick={resetFilters}
                        className="mt-4 inline-flex items-center rounded-md bg-[#2ca01c] px-4 py-2 text-white"
                      >
                        <MdClear className="mr-2" /> Réinitialiser les filtres
                      </button>
                    )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {currentDoctors.map((doctor) => renderDoctorCard(doctor))}
                  </div>

                  {filteredDoctors.length > itemsPerPage && renderPagination()}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Modal pour le formulaire */}
      <Modal isOpen={showForm} onClose={closeForm} width="2xl">
        {formContent}
      </Modal>
    </div>
  );
};

export default Doctors;
